import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Database, RefreshCw, Trash2, HardDrive, BarChart3, CreditCard, Eye, EyeOff, CheckCircle, XCircle, Loader2, Megaphone, Bell, AlertTriangle, Info } from 'lucide-react';

const Settings = () => {
  const [dbStats, setDbStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  
  // Notice Board state
  const [noticeSettings, setNoticeSettings] = useState({
    enabled: false,
    title: 'Important Notice',
    content: '',
    type: 'info',
    show_close_button: true,
    auto_dismiss: false,
    dismiss_after_seconds: 10
  });
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [noticeSaving, setNoticeSaving] = useState(false);
  
  // Payment settings state
  const [paymentSettings, setPaymentSettings] = useState({
    key_id: '',
    key_secret: '',
    webhook_secret: '',
    is_test_mode: true
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentTesting, setPaymentTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);

  const fetchDatabaseStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/database/stats');
      setDbStats(response.data.collections);
    } catch (error) {
      toast.error('Failed to fetch database stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentSettings = async () => {
    setPaymentLoading(true);
    try {
      const response = await axios.get('/admin/payment-settings');
      setPaymentSettings({
        key_id: response.data.key_id || '',
        key_secret: '', // Don't show actual secret
        webhook_secret: '',
        is_test_mode: response.data.is_test_mode !== false
      });
      setIsConfigured(response.data.is_configured);
    } catch (error) {
      console.error('Failed to fetch payment settings');
    } finally {
      setPaymentLoading(false);
    }
  };

  const fetchNoticeSettings = async () => {
    setNoticeLoading(true);
    try {
      const response = await axios.get('/admin/notice');
      setNoticeSettings({
        enabled: response.data.enabled || false,
        title: response.data.title || 'Important Notice',
        content: response.data.content || '',
        type: response.data.type || 'info',
        show_close_button: response.data.show_close_button !== false,
        auto_dismiss: response.data.auto_dismiss || false,
        dismiss_after_seconds: response.data.dismiss_after_seconds || 10
      });
    } catch (error) {
      console.error('Failed to fetch notice settings');
    } finally {
      setNoticeLoading(false);
    }
  };

  useEffect(() => {
    fetchDatabaseStats();
    fetchPaymentSettings();
    fetchNoticeSettings();
  }, []);

  const handleOptimize = async () => {
    if (!window.confirm('This will rebuild all database indexes. Continue?')) return;
    setOptimizing(true);
    try {
      await axios.post('/admin/database/optimize');
      toast.success('Database optimization completed');
      fetchDatabaseStats();
    } catch (error) {
      toast.error('Failed to optimize database');
    } finally {
      setOptimizing(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('This will delete session data older than 30 days. Continue?')) return;
    setCleaning(true);
    try {
      const response = await axios.post('/admin/database/cleanup');
      toast.success(`Cleanup completed: ${response.data.sessions_deleted} sessions, ${response.data.login_attempts_deleted} login attempts deleted`);
      fetchDatabaseStats();
    } catch (error) {
      toast.error('Failed to cleanup old data');
    } finally {
      setCleaning(false);
    }
  };

  const handleSavePaymentSettings = async () => {
    if (!paymentSettings.key_id) {
      toast.error('Please enter Razorpay Key ID');
      return;
    }
    
    setPaymentSaving(true);
    try {
      const response = await axios.put('/admin/payment-settings', paymentSettings);
      toast.success('Payment settings saved');
      
      if (response.data.connection_status === 'success') {
        toast.success('Razorpay connection verified');
        setIsConfigured(true);
      } else if (response.data.connection_status.startsWith('failed')) {
        toast.error(`Connection test failed: ${response.data.connection_status}`);
      }
      
      fetchPaymentSettings();
    } catch (error) {
      toast.error('Failed to save payment settings');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setPaymentTesting(true);
    try {
      const response = await axios.post('/admin/payment-settings/test');
      if (response.data.status === 'success') {
        toast.success('Razorpay connection successful!');
      } else {
        toast.error(`Connection failed: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setPaymentTesting(false);
    }
  };

  const handleSaveNotice = async () => {
    setNoticeSaving(true);
    try {
      await axios.put('/admin/notice', noticeSettings);
      toast.success(noticeSettings.enabled ? 'Notice board enabled and saved!' : 'Notice board saved (disabled)');
      fetchNoticeSettings();
    } catch (error) {
      toast.error('Failed to save notice settings');
    } finally {
      setNoticeSaving(false);
    }
  };

  const getNoticeTypeIcon = (type) => {
    switch (type) {
      case 'urgent': return <AlertTriangle className="w-4 h-4" />;
      case 'warning': return <Bell className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getTotalSize = () => {
    if (!dbStats) return '0 MB';
    const total = dbStats.reduce((sum, coll) => sum + (coll.size_mb || 0), 0);
    return total.toFixed(2) + ' MB';
  };

  const getTotalDocs = () => {
    if (!dbStats) return 0;
    return dbStats.reduce((sum, coll) => sum + (coll.document_count || 0), 0).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-800">Settings</h2>
        <p className="text-gray-600 mt-1">System configuration and database management</p>
      </div>

      {/* Notice Board Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5" />
            Notice Board
          </CardTitle>
          <CardDescription>Display important announcements to students</CardDescription>
        </CardHeader>
        <CardContent>
          {noticeLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid="notice-enable-section">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${noticeSettings.enabled ? 'bg-green-100' : 'bg-gray-200'}`}>
                    <Megaphone className={`w-5 h-5 ${noticeSettings.enabled ? 'text-green-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <Label className="font-medium">Enable Notice Board</Label>
                    <p className="text-sm text-gray-500">Show floating notice to all students</p>
                  </div>
                </div>
                <Switch
                  checked={noticeSettings.enabled}
                  onCheckedChange={(checked) => setNoticeSettings({ ...noticeSettings, enabled: checked })}
                  data-testid="notice-enable-switch"
                />
              </div>

              {/* Notice Form */}
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="notice_title">Title</Label>
                  <Input
                    id="notice_title"
                    placeholder="e.g., Exam Schedule Update"
                    value={noticeSettings.title}
                    onChange={(e) => setNoticeSettings({ ...noticeSettings, title: e.target.value })}
                    data-testid="notice-title-input"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="notice_content">Content</Label>
                  <Textarea
                    id="notice_content"
                    placeholder="Enter your notice message here..."
                    value={noticeSettings.content}
                    onChange={(e) => setNoticeSettings({ ...noticeSettings, content: e.target.value })}
                    rows={4}
                    data-testid="notice-content-input"
                  />
                  <p className="text-xs text-gray-500">Supports line breaks for formatting</p>
                </div>

                {/* Notice Type */}
                <div className="grid gap-2">
                  <Label>Notice Type</Label>
                  <div className="flex flex-wrap gap-2" data-testid="notice-type-selector">
                    {[
                      { value: 'info', label: 'Info', color: 'bg-blue-500', icon: <Info className="w-4 h-4" /> },
                      { value: 'warning', label: 'Warning', color: 'bg-amber-500', icon: <Bell className="w-4 h-4" /> },
                      { value: 'urgent', label: 'Urgent', color: 'bg-red-500', icon: <AlertTriangle className="w-4 h-4" /> }
                    ].map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setNoticeSettings({ ...noticeSettings, type: type.value })}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                          noticeSettings.type === type.value 
                            ? `${type.color} text-white border-transparent` 
                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`notice-type-${type.value}`}
                      >
                        {type.icon}
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Additional Options */}
                <div className="grid sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={noticeSettings.show_close_button}
                      onCheckedChange={(checked) => setNoticeSettings({ ...noticeSettings, show_close_button: checked })}
                      data-testid="notice-show-close-switch"
                    />
                    <div>
                      <Label className="text-sm">Show Close Button</Label>
                      <p className="text-xs text-gray-500">Allow students to dismiss</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={noticeSettings.auto_dismiss}
                      onCheckedChange={(checked) => setNoticeSettings({ ...noticeSettings, auto_dismiss: checked })}
                      data-testid="notice-auto-dismiss-switch"
                    />
                    <div>
                      <Label className="text-sm">Auto Dismiss</Label>
                      <p className="text-xs text-gray-500">Auto-hide after seconds</p>
                    </div>
                  </div>
                </div>

                {noticeSettings.auto_dismiss && (
                  <div className="grid gap-2">
                    <Label>Dismiss After (seconds)</Label>
                    <Input
                      type="number"
                      min="5"
                      max="60"
                      value={noticeSettings.dismiss_after_seconds}
                      onChange={(e) => setNoticeSettings({ ...noticeSettings, dismiss_after_seconds: parseInt(e.target.value) || 10 })}
                      data-testid="notice-dismiss-seconds-input"
                    />
                  </div>
                )}
              </div>

              {/* Preview */}
              {noticeSettings.enabled && noticeSettings.content && (
                <div className="p-4 border rounded-lg" data-testid="notice-preview">
                  <Label className="text-sm text-gray-500 mb-2 block">Preview</Label>
                  <div className={`rounded-lg overflow-hidden ${
                    noticeSettings.type === 'urgent' ? 'bg-gradient-to-r from-red-500 to-rose-600' :
                    noticeSettings.type === 'warning' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                    'bg-gradient-to-r from-blue-500 to-indigo-600'
                  }`}>
                    <div className="flex items-center gap-2 px-4 py-2 bg-black/10 text-white">
                      {getNoticeTypeIcon(noticeSettings.type)}
                      <span className="font-bold text-sm uppercase">{noticeSettings.title}</span>
                    </div>
                    <div className="px-4 py-3 text-white text-sm">
                      {noticeSettings.content.split('\n').map((line, i) => (
                        <span key={i}>{line}<br/></span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <Button onClick={handleSaveNotice} disabled={noticeSaving} className="w-full sm:w-auto" data-testid="notice-save-btn">
                {noticeSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Megaphone className="w-4 h-4 mr-2" />
                    {noticeSettings.enabled ? 'Publish Notice' : 'Save Settings'}
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Gateway Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Gateway (Razorpay)
          </CardTitle>
          <CardDescription>Configure your Razorpay API keys for payment processing</CardDescription>
        </CardHeader>
        <CardContent>
          {paymentLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Status Indicator */}
              <div className={`flex items-center gap-2 p-3 rounded-lg ${isConfigured ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                {isConfigured ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Razorpay is configured and ready</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">Razorpay not configured - payments will run in mock mode</span>
                  </>
                )}
              </div>

              {/* API Keys Form */}
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="key_id">Key ID</Label>
                  <Input
                    id="key_id"
                    placeholder="rzp_test_xxxxxxxxxxxxxxxx"
                    value={paymentSettings.key_id}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, key_id: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">Your Razorpay Key ID (starts with rzp_test_ or rzp_live_)</p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="key_secret">Key Secret</Label>
                  <div className="relative">
                    <Input
                      id="key_secret"
                      type={showSecrets ? 'text' : 'password'}
                      placeholder="Enter new secret or leave blank to keep existing"
                      value={paymentSettings.key_secret}
                      onChange={(e) => setPaymentSettings({ ...paymentSettings, key_secret: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowSecrets(!showSecrets)}
                    >
                      {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">Leave blank to keep existing secret</p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="webhook_secret">Webhook Secret (Optional)</Label>
                  <Input
                    id="webhook_secret"
                    type={showSecrets ? 'text' : 'password'}
                    placeholder="For webhook signature verification"
                    value={paymentSettings.webhook_secret}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, webhook_secret: e.target.value })}
                  />
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Switch
                    checked={paymentSettings.is_test_mode}
                    onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, is_test_mode: checked })}
                  />
                  <div>
                    <Label className="font-medium">Test Mode</Label>
                    <p className="text-xs text-gray-500">Enable this for testing with test API keys</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleSavePaymentSettings} disabled={paymentSaving}>
                  {paymentSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
                <Button variant="outline" onClick={handleTestConnection} disabled={paymentTesting || !isConfigured}>
                  {paymentTesting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </Button>
              </div>

              {/* Help Box */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-sm text-blue-800 mb-2">How to get Razorpay API Keys</h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noopener noreferrer" className="underline">Razorpay Dashboard → Settings → API Keys</a></li>
                  <li>Generate a new key pair (Test or Live mode)</li>
                  <li>Copy the Key ID and Key Secret</li>
                  <li>Paste them here and save</li>
                </ol>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Database Management Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Database Management
          </CardTitle>
          <CardDescription>Monitor and optimize your database performance</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <HardDrive className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-sm text-gray-600">Total Size</p>
              <p className="text-xl font-bold text-blue-700">{getTotalSize()}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <BarChart3 className="w-6 h-6 mx-auto mb-2 text-green-600" />
              <p className="text-sm text-gray-600">Total Documents</p>
              <p className="text-xl font-bold text-green-700">{getTotalDocs()}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg text-center">
              <Database className="w-6 h-6 mx-auto mb-2 text-purple-600" />
              <p className="text-sm text-gray-600">Collections</p>
              <p className="text-xl font-bold text-purple-700">{dbStats?.length || 0}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <RefreshCw className="w-6 h-6 mx-auto mb-2 text-orange-600" />
              <p className="text-sm text-gray-600">Auto-Cleanup</p>
              <p className="text-xl font-bold text-orange-700">30 Days</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Button onClick={fetchDatabaseStats} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Stats
            </Button>
            <Button onClick={handleOptimize} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" disabled={optimizing}>
              <Database className={`w-4 h-4 mr-2 ${optimizing ? 'animate-pulse' : ''}`} />
              {optimizing ? 'Optimizing...' : 'Rebuild Indexes'}
            </Button>
            <Button onClick={handleCleanup} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" disabled={cleaning}>
              <Trash2 className={`w-4 h-4 mr-2 ${cleaning ? 'animate-pulse' : ''}`} />
              {cleaning ? 'Cleaning...' : 'Cleanup Old Sessions'}
            </Button>
          </div>

          {/* Collection Details Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Collection</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Documents</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Size</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Indexes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                      Loading...
                    </td>
                  </tr>
                ) : dbStats?.map((coll) => (
                  <tr key={coll.name} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{coll.name}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{coll.document_count?.toLocaleString() || 'N/A'}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{coll.size_mb ? `${coll.size_mb} MB` : 'N/A'}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{coll.index_count || 0}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* System Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>System Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Backend</p>
              <p className="font-semibold">FastAPI (Async)</p>
            </div>
            <div>
              <p className="text-gray-600">Frontend</p>
              <p className="font-semibold">React + Tailwind CSS</p>
            </div>
            <div>
              <p className="text-gray-600">Database</p>
              <p className="font-semibold">MongoDB with TTL Indexes</p>
            </div>
            <div>
              <p className="text-gray-600">Payment Gateway</p>
              <p className="font-semibold">Razorpay</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
